/** Create modal: task or event, with a tolerant "when" parser. */
import { $$, byId, on, toast } from "../lib/dom";
import { addDays, addMinutes, startOfDay } from "../lib/time";
import { createEvent, createNote, createTask } from "../data/store";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import { dictateInto } from "./shell";
import { scheduleTaskIds } from "./meeting";

type Kind = "task" | "event" | "meeting" | "note" | "focus";
let kind: Kind = "task";
type Intent = { kind: "task" | "event" | "meeting" | "note"; title: string; when: string; durationMin: number; notes: string; confidence: number; mode: string };
const parseIntent = httpsCallable<{ text: string; timezone: string }, Intent>(functions, "parseIntent");
let understood: Intent | null = null;

/** Accepts "today 3pm", "tomorrow", "fri 10:30", "in 2 days", "2026-09-14 14:00". Returns null when unsure. */
export function parseWhen(text: string, now = new Date()): Date | null {
  const s = text.trim().toLowerCase();
  if (!s) return null;
  let day = startOfDay(now);
  let matched = false;
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) { day = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])); matched = true; }
  else if (/\btomorrow\b/.test(s)) { day = addDays(day, 1); matched = true; }
  else if (/\btoday\b/.test(s)) matched = true;
  else {
    const inDays = /in (\d+) days?/.exec(s);
    if (inDays) { day = addDays(day, Number(inDays[1])); matched = true; }
    const wd = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"].findIndex((d) => new RegExp(`\\b${d}`).test(s));
    if (wd >= 0) { let diff = (wd - day.getDay() + 7) % 7; if (diff === 0) diff = 7; day = addDays(day, diff); matched = true; }
  }
  const time = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/.exec(s.replace(/\d{4}-\d{2}-\d{2}/, ""));
  if (time && (time[3] || time[2])) {
    let h = Number(time[1]);
    if (time[3] === "pm" && h < 12) h += 12;
    if (time[3] === "am" && h === 12) h = 0;
    day.setHours(h, Number(time[2] ?? 0), 0, 0);
    matched = true;
  } else if (matched) day.setHours(9, 0, 0, 0);
  return matched ? day : null;
}

export function openCreate(preset?: Kind) {
  if (preset) setKind(preset);
  const modal = byId("createModal")!;
  modal.classList.add("show");
  modal.setAttribute("aria-hidden", "false");
  understood = null; status(""); byId<HTMLInputElement>("createWhen")!.dataset.iso = "";
  setTimeout(() => byId<HTMLInputElement>("createName")?.focus(), 80);
}
export function closeCreate() {
  const modal = byId("createModal")!;
  modal.classList.remove("show");
  modal.setAttribute("aria-hidden", "true");
}
function setKind(k: Kind) {
  kind = k;
  $$(".type-chip").forEach((b) => b.classList.toggle("active", (b as HTMLElement).dataset.kind === (k === "focus" ? "event" : k)));
  byId("createTitle")!.textContent = `Create a new ${k === "focus" ? "focus block" : k}`;
  const isNote = k === "note";
  byId("createNotesField")!.hidden = !isNote && !(understood?.notes);
  byId("createAutoWrap")!.hidden = isNote;
  byId<HTMLSelectElement>("createDuration")!.closest<HTMLElement>(".field")!.hidden = isNote;
  if (!isNote && !understood) setDuration(k === "meeting" ? 60 : 30);
  byId<HTMLInputElement>("createWhen")!.closest<HTMLElement>(".field")!.hidden = isNote;
  byId("createSubmit")!.textContent = isNote ? "Save note" : k === "task" ? "Create task" : k === "meeting" ? "Create meeting" : "Create";
}

/** The duration select carries the common choices; any other number the model returns is added on the fly. */
function setDuration(min: number) {
  const sel = byId<HTMLSelectElement>("createDuration")!;
  if (![...sel.options].some((o) => Number(o.value) === min)) { const o = document.createElement("option"); o.value = String(min); o.textContent = min >= 60 && min % 60 === 0 ? `${min / 60} hour${min > 60 ? "s" : ""}` : `${min} min`; sel.append(o); }
  sel.value = String(min);
}
const status = (text: string, tone: "busy" | "ok" | "warn" | "" = "") => { const el = byId("createStatus")!; el.hidden = !text; el.textContent = text; el.className = `say-status ${tone}`; };
const fmtWhen = (d: Date) => d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Ask Timo to turn whatever was said or typed into the fields. One round trip, then one click. */
async function understand(text: string) {
  if (!text.trim()) return;
  status("Timo is working out the details…", "busy");
  try {
    const { data } = await parseIntent({ text, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    understood = data;
    setKind(data.kind);
    byId<HTMLInputElement>("createName")!.value = data.title;
    const when = data.when ? new Date(data.when) : parseWhen(text);
    byId<HTMLInputElement>("createWhen")!.value = when ? fmtWhen(when) : "";
    byId<HTMLInputElement>("createWhen")!.dataset.iso = when ? when.toISOString() : "";
    setDuration(data.durationMin || (data.kind === "meeting" ? 60 : 30));
    if (data.notes) { byId("createNotesField")!.hidden = false; byId<HTMLTextAreaElement>("createNotes")!.value = data.notes; }
    status(`${data.kind === "meeting" ? "Meeting" : data.kind === "note" ? "Note" : data.kind === "event" ? "Event" : "Task"}${when ? ` · ${fmtWhen(when)}` : " · Timo will find time"}${data.durationMin && data.kind !== "note" ? ` · ${data.durationMin} min` : ""}. Check and press Create`, "ok");
    byId("createSubmit")!.focus();
  } catch (err) {
    understood = null;
    const when = parseWhen(text);
    if (when) { byId<HTMLInputElement>("createWhen")!.value = fmtWhen(when); byId<HTMLInputElement>("createWhen")!.dataset.iso = when.toISOString(); }
    status((err as Error).message.includes("budget") ? "Daily AI budget reached. Filled what could be read from the words" : "Filled what could be read from the words. Check the fields", "warn");
  }
}

export function setupCreate() {
  document.addEventListener("timo:create", ((e: CustomEvent<"task" | "event" | "focus">) => openCreate(e.detail)) as EventListener);
  // Clicking an empty slot on the calendar opens this already pointed at that time
  document.addEventListener("timo:create-at", ((e: CustomEvent<{ start: string }>) => {
    const at = new Date(e.detail.start);
    openCreate("event");
    const when = byId<HTMLInputElement>("createWhen")!;
    when.value = at.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
    when.dataset.iso = at.toISOString();
  }) as EventListener);
  $$(".open-create").forEach((b) => on(b, "click", () => openCreate()));
  on(byId("closeCreate"), "click", closeCreate);
  on(byId("cancelCreate"), "click", closeCreate);
  on(byId("createModal"), "click", (e) => e.target === byId("createModal") && closeCreate());
  document.addEventListener("timo:escape", closeCreate);
  $$(".type-chip").forEach((b) => on(b, "click", () => setKind(((b as HTMLElement).dataset.kind as Kind) ?? "task")));
  // Say it: dictation lands in the box, then Timo fills the rest.
  on(byId("createVoice"), "click", () => {
    const input = byId<HTMLInputElement>("createName")!;
    const ok = dictateInto(input, byId("createVoice")!);
    if (!ok) return;
    status("Listening…", "busy");
    const poll = window.setInterval(() => { if (!byId("createVoice")!.classList.contains("listening")) { window.clearInterval(poll); if (input.value.trim()) void understand(input.value); else status(""); } }, 200);
  });
  // Typed a sentence and paused? Offer the same understanding without a click.
  let typingTimer: number | undefined;
  on(byId("createName"), "input", () => { understood = null; byId<HTMLInputElement>("createWhen")!.dataset.iso = ""; window.clearTimeout(typingTimer); const v = byId<HTMLInputElement>("createName")!.value; if (v.trim().split(/\s+/).length >= 4) typingTimer = window.setTimeout(() => void understand(v), 900); });
  on(byId("createWhen"), "input", () => { byId<HTMLInputElement>("createWhen")!.dataset.iso = ""; });
  on(byId("createForm"), "submit", async (e) => {
    e.preventDefault();
    const title = byId<HTMLInputElement>("createName")!.value.trim();
    if (!title) return;
    const whenEl = byId<HTMLInputElement>("createWhen")!;
    const when = whenEl.dataset.iso ? new Date(whenEl.dataset.iso) : parseWhen(whenEl.value);
    const durationMin = Math.max(5, Number(byId<HTMLSelectElement>("createDuration")!.value) || 30);
    const notes = byId<HTMLTextAreaElement>("createNotes")!.value.trim();
    const auto = byId<HTMLInputElement>("createAuto")!.checked;
    if (kind === "note") {
      await createNote({ title, body: notes });
      toast("Note saved");
    } else if (kind === "task") {
      const id = await createTask({ title, notes, dueAt: when, durationMin, source: "user" });
      if (auto) { closeCreate(); (e.target as HTMLFormElement).reset(); understood = null; status(""); await scheduleTaskIds([id]); return; }
      toast(when ? `${title} added. Due ${when.toLocaleDateString()}` : `${title} added`);
    } else {
      const start = when ?? addMinutes(new Date(), 60 - (new Date().getMinutes() % 60));
      const isMeeting = kind === "meeting";
      await createEvent({ title, start, end: addMinutes(start, durationMin || (isMeeting ? 60 : 60)), kind: isMeeting ? "meeting" : kind === "focus" ? "focus" : "event", flexible: kind === "focus", source: "user", googleId: null, taskId: null, notes });
      toast(`${title} is on your calendar · ${fmtWhen(start)}`);
    }
    (e.target as HTMLFormElement).reset();
    understood = null; status("");
    closeCreate();
  });
  // Recording is the other way to capture a meeting: start it from here and skip the form.
  on(byId("createRecord"), "click", () => { const title = byId<HTMLInputElement>("createName")!.value.trim(); closeCreate(); document.dispatchEvent(new CustomEvent("timo:record", { detail: kind === "meeting" && title ? title : undefined })); });
  // "+ Add commitment" on Today
  on(byId("addCommitment"), "click", () => openCreate("task"));
}
